import React, { useState, useEffect } from "react";
import { collection, doc, runTransaction, serverTimestamp, getDocs, deleteDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/Firebase";

export function AddCoopound() {
  const [code, setCode] = useState("");
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [editingCode, setEditingCode] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editStatus, setEditStatus] = useState("active");

  const resetForm = () => {
    setCode("");
    setAmount("");
    setError("");
  };

  const handleSave = async () => {
    const trimmed = code.trim();
    const amt = parseFloat(amount);
    if (!trimmed) {
      setError("Please enter a coopound code.");
      return;
    }
    if (isNaN(amt) || amt <= 0) {
      setError("Please enter a valid positive amount.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const colRef = collection(db, "coopound local");
      const docRef = doc(colRef, trimmed);
      const data = {
        Code: trimmed,
        Amount: amt,
        CreatedAt: serverTimestamp(),
        Status: "active",
      };

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        if (snap.exists()) throw new Error("Coopound code already exists.");
        tx.set(docRef, data);
      });

      alert("Coopound created successfully.");
      resetForm();
      await fetchCoupons();
    } catch (err) {
      setError("Database error: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCoupons = async () => {
    try {
      const snapshot = await getDocs(collection(db, "coopound local"));
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by CreatedAt desc if available
      list.sort((a, b) => {
        const ta = a.CreatedAt?.toDate ? a.CreatedAt.toDate().getTime() : 0;
        const tb = b.CreatedAt?.toDate ? b.CreatedAt.toDate().getTime() : 0;
        return tb - ta;
      });
      setCoupons(list);
    } catch (err) {
      console.warn("Fetch coupons error:", err.message);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const startEdit = (c) => {
    setEditingCode(c.id);
    setEditCode(c.id);
    setEditAmount(c.Amount?.toString() || "");
    setEditStatus(c.Status || "active");
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditCode("");
    setEditAmount("");
    setEditStatus("active");
  };

  const saveEdit = async () => {
    if (!editingCode) return;
    const newCode = editCode.trim();
    const amt = parseFloat(editAmount);
    if (!newCode) {
      setError("Please enter a coopound code.");
      return;
    }
    if (isNaN(amt) || amt <= 0) {
      setError("Please enter a valid positive amount.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const colRef = collection(db, "coopound local");
      const oldRef = doc(colRef, editingCode);
      const newRef = doc(colRef, newCode);
      const data = {
        Code: newCode,
        Amount: amt,
        Status: editStatus,
        UpdatedAt: serverTimestamp(),
      };

      if (newCode === editingCode) {
        await updateDoc(oldRef, data);
      } else {
        await runTransaction(db, async (tx) => {
          const existing = await tx.get(newRef);
          if (existing.exists()) throw new Error("Target coopound code already exists.");
          tx.set(newRef, { ...data, CreatedAt: serverTimestamp() });
          tx.delete(oldRef);
        });
      }

      alert("Coopound updated.");
      cancelEdit();
      await fetchCoupons();
    } catch (err) {
      setError("Update error: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this coopound?")) return;
    setIsLoading(true);
    setError("");
    try {
      await deleteDoc(doc(collection(db, "coopound local"), id));
      alert("Coopound deleted.");
      await fetchCoupons();
    } catch (err) {
      setError("Delete error: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto my-10 p-8 bg-white rounded-xl shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">Add Coopound</h1>

      {error && (
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-6 text-center">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-gray-700 font-medium mb-1">Coopound Code</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g., NEWYEAR2026"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">Amount (₹)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g., 250"
            min="0"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
          >
            {isLoading ? "Saving..." : "Create Coopound"}
          </button>
          <button
            type="button"
            onClick={resetForm}
            className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition"
          >
            Reset
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          Note: Document ID is the coopound code. "CreatedAt" is recorded with server time.
        </p>
      </div>

      {/* List & CRUD */}
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-2xl font-semibold">Existing Coopounds</h2>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code or status"
            className="ml-auto w-64 px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 border-b text-left">Code</th>
                <th className="px-4 py-2 border-b text-left">Amount (₹)</th>
                <th className="px-4 py-2 border-b text-left">Created</th>
                <th className="px-4 py-2 border-b text-left">Status</th>
                <th className="px-4 py-2 border-b text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons
                .filter((c) => {
                  const s = search.trim().toLowerCase();
                  if (!s) return true;
                  return (
                    c.id.toLowerCase().includes(s) ||
                    (c.Status || "").toLowerCase().includes(s)
                  );
                })
                .map((c) => {
                  const createdStr = c.CreatedAt?.toDate
                    ? c.CreatedAt.toDate().toLocaleString()
                    : "-";
                  const isEditing = editingCode === c.id;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border-b">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editCode}
                            onChange={(e) => setEditCode(e.target.value)}
                            className="w-full px-2 py-1 border rounded"
                          />
                        ) : (
                          c.id
                        )}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            className="w-full px-2 py-1 border rounded"
                          />
                        ) : (
                          c.Amount
                        )}
                      </td>
                      <td className="px-4 py-2 border-b">{createdStr}</td>
                      <td className="px-4 py-2 border-b">
                        {isEditing ? (
                          <select
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                            className="px-2 py-1 border rounded"
                          >
                            <option value="active">active</option>
                            <option value="inactive">inactive</option>
                            <option value="expired">expired</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-1 rounded text-xs ${c.Status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{c.Status || '-'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 border-b">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <button onClick={saveEdit} disabled={isLoading} className="bg-black text-white px-3 py-1 rounded text-sm">Save</button>
                            <button onClick={cancelEdit} className="bg-gray-500 text-white px-3 py-1 rounded text-sm">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button onClick={() => startEdit(c)} className="bg-black text-white px-3 py-1 rounded text-sm">Edit</button>
                            <button onClick={() => handleDelete(c.id)} className="bg-red-500 text-white px-3 py-1 rounded text-sm">Delete</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              {coupons.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-4 py-6 text-center text-gray-600">No coopounds found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AddCoopound;
